/**
 * Sistema de Fotos com Marca D'água - HouseBit Integration
 * 
 * Suporta 3 formatos de fotos para retrocompatibilidade:
 * 1. String simples: "url.jpg"
 * 2. Formato antigo: { id: "...", url: "...", ordem: 0 }
 * 3. Formato novo: { url_original: "...", url_marca_dagua: "...", tem_marca_dagua: true, ordem: 0 }
 */

/**
 * Formato novo (HouseBit 2025)
 */
export interface FotoImovel {
  url_original: string;
  url_marca_dagua?: string | null;
  tem_marca_dagua?: boolean;
  ordem?: number;
}

/**
 * Formato antigo (para retrocompatibilidade)
 */
export interface FotoImovelAntiga {
  id: string;
  url: string;
  descricao?: string;
  ordem?: number;
}

/**
 * Tipos aceitos para uma foto individual
 */
export type FotoInput = string | FotoImovel | FotoImovelAntiga;

/**
 * Extrai a URL de uma foto individual
 * 
 * @param foto - Pode ser string (formato antigo) ou objeto (formato novo/antigo)
 * @param aplicarMarcaDagua - Se true, retorna url_marca_dagua quando disponível
 * @returns URL da foto para exibição
 * 
 * @example
 * // Formato string
 * getFotoUrl("foto.jpg", false) // => "foto.jpg"
 * 
 * // Formato antigo (objeto com url)
 * getFotoUrl({ id: "1", url: "foto.jpg" }, false) // => "foto.jpg"
 * 
 * // Formato novo (com marca d'água disponível)
 * getFotoUrl({ 
 *   url_original: "original.jpg", 
 *   url_marca_dagua: "marca.jpg",
 *   tem_marca_dagua: true 
 * }, true) // => "marca.jpg"
 * 
 * // Formato novo (sem marca d'água)
 * getFotoUrl({ 
 *   url_original: "original.jpg", 
 *   url_marca_dagua: null,
 *   tem_marca_dagua: false 
 * }, true) // => "original.jpg" (fallback)
 */
export function getFotoUrl(
  foto: FotoInput,
  aplicarMarcaDagua: boolean = false
): string {
  // Caso 1: String simples (formato mais antigo)
  if (typeof foto === 'string') {
    return foto;
  }
  
  // Caso 2: Objeto com url_original (formato novo HouseBit 2025)
  if ('url_original' in foto) {
    // Se deve aplicar marca d'água E ela existe, usar url_marca_dagua
    if (aplicarMarcaDagua && foto.url_marca_dagua && foto.tem_marca_dagua) {
      return foto.url_marca_dagua;
    }
    // Caso contrário, sempre retornar url_original
    return foto.url_original;
  }
  
  // Caso 3: Objeto com url (formato antigo)
  if ('url' in foto && foto.url) {
    return foto.url;
  }
  
  // Fallback: retornar string vazia (nunca deve acontecer)
  console.warn('getFotoUrl: formato de foto não reconhecido', foto);
  return '';
}

/**
 * Extrai a primeira foto de um imóvel
 * 
 * @param fotos - Array de fotos do imóvel
 * @param aplicarMarcaDagua - Se true, usa url_marca_dagua quando disponível
 * @param placeholder - URL do placeholder caso não tenha fotos
 * @returns URL da primeira foto ou placeholder
 * 
 * @example
 * const fotos = [
 *   { url_original: "foto1.jpg", url_marca_dagua: "foto1_marca.jpg", tem_marca_dagua: true }
 * ];
 * 
 * getPrimeiraFoto(fotos, true, "/placeholder.svg") 
 * // => "foto1_marca.jpg"
 * 
 * getPrimeiraFoto(fotos, false, "/placeholder.svg") 
 * // => "foto1.jpg"
 * 
 * getPrimeiraFoto([], false, "/placeholder.svg") 
 * // => "/placeholder.svg"
 */
export function getPrimeiraFoto(
  fotos: FotoInput[] | null | undefined,
  aplicarMarcaDagua: boolean = false,
  placeholder: string = '/img-default.jpg'
): string {
  if (!fotos || !Array.isArray(fotos) || fotos.length === 0) {
    return placeholder;
  }
  
  return getFotoUrl(fotos[0], aplicarMarcaDagua);
}

/**
 * Converte array de fotos para array de URLs
 * 
 * @param fotos - Array de fotos do imóvel
 * @param aplicarMarcaDagua - Se true, usa url_marca_dagua quando disponível
 * @returns Array de URLs das fotos
 * 
 * @example
 * const fotos = [
 *   { url_original: "foto1.jpg", url_marca_dagua: "foto1_marca.jpg", tem_marca_dagua: true },
 *   { url_original: "foto2.jpg", url_marca_dagua: null, tem_marca_dagua: false },
 *   "foto3.jpg" // Formato antigo
 * ];
 * 
 * getFotosUrls(fotos, true) 
 * // => ["foto1_marca.jpg", "foto2.jpg", "foto3.jpg"]
 * 
 * getFotosUrls(fotos, false) 
 * // => ["foto1.jpg", "foto2.jpg", "foto3.jpg"]
 */
export function getFotosUrls(
  fotos: FotoInput[] | null | undefined,
  aplicarMarcaDagua: boolean = false
): string[] {
  if (!fotos || !Array.isArray(fotos)) {
    return [];
  }
  
  return fotos
    .map(foto => getFotoUrl(foto, aplicarMarcaDagua))
    .filter(url => url && url.trim().length > 0);
}
